import React, { useState } from 'react';
import { signInWithEmail } from '../../services/auth';
import AuthPageShell, { AuthFormHeading, AuthRule, AuthCrossLink } from './AuthPageShell';
import GoogleSignInButton from './GoogleSignInButton';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface LoginFormProps {
    onSwitchToRegister: () => void;
    onSwitchToForgotPassword: () => void;
}

function LoginForm({ onSwitchToRegister, onSwitchToForgotPassword }: LoginFormProps) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            setError('Please fill in all fields');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await signInWithEmail(email, password);
            // Success is handled by the auth state listener.
        } catch (err: any) {
            setError(err.message || 'Failed to sign in');
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell>
            <AuthFormHeading title="Log In" />

            <form onSubmit={handleSubmit} className="flex flex-col">
                <Input
                    variant="line"
                    type="email"
                    placeholder="Email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                />
                <Input
                    variant="line"
                    type="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                />

                {error && <p className="mt-4 text-meta font-semibold text-alert">{error}</p>}

                <Button type="submit" variant="primary" size="lg" fullWidth className="mt-6" disabled={isLoading}>
                    {isLoading ? 'Signing in…' : 'Log In'}
                </Button>

                <button
                    type="button"
                    onClick={onSwitchToForgotPassword}
                    disabled={isLoading}
                    className="mt-4 self-start text-[13px] text-[#6E6E6E] underline transition-colors hover:text-ink-secondary"
                >
                    Forgot password?
                </button>
            </form>

            <AuthRule className="mt-7" />

            <div className="mt-6">
                <GoogleSignInButton onError={setError} disabled={isLoading} label="Log in with Google" />
            </div>

            <AuthRule className="mt-7" />

            <AuthCrossLink prompt="New here?" label="Create account" onClick={onSwitchToRegister} />
        </AuthPageShell>
    );
}

export default LoginForm;
